// AnimGob — a fire-and-forget animation gob (explosions, smoke puffs' big brother). SIM
// STATE despite looking cosmetic: it allocates a gid, sits in the master gob list (so it
// shifts TrackState iteration), advances per update, and can notify a StateMachine on
// completion. game/GameObjects.cpp:837-989 + game/ht.h:6390-6426.

import type { SimContext } from '../context.ts';
import {
  Gob,
  kfGobStateMachine,
  kfGobLayerSmokeFire,
  kfGobLayerSurfaceDecal,
  kfGobLayerDepthSorted,
} from './gob.ts';
import { kgtAnimation } from './gobtypes.ts';
import { knLimitSupport } from './gobmgr.ts';
import { Animation, AnimData } from '../anim/animation.ts';
import {
  Message,
  ksmidNull,
  kmidAnimationComplete,
  knHandled,
  knNotHandled,
  knDeleted,
  kstReservedGlobal,
  kmidReservedUpdate,
} from '../sm/message.ts';

// game/ht.h:6422-6426
export const kfAnmDeleteWhenDone = 0x0001;
export const kfAnmLoop = 0x0002;
export const kfAnmSmokeFireLayer = 0x0004;
export const kfAnmSurfaceDecalLayer = 0x0008;

export class AnimGob extends Gob {
  m_smidNotify = ksmidNull;
  m_wfAnm = 0;
  m_ani = new Animation();

  // game/GameObjects.cpp:856-860
  constructor(ctx: SimContext) {
    super(ctx);
    this.m_ff = (this.m_ff | kfGobStateMachine) >>> 0;
  }

  override GetType(): number {
    return kgtAnimation;
  } // :926-929

  // game/GameObjects.cpp:866-877 — level-file form: "%*d ,%s,%d ,%d" handled by the level
  // loader, which resolves the AnimData and calls Init.

  // game/GameObjects.cpp:879-914
  Init(
    wx: number,
    wy: number,
    wfAnm: number,
    panid: AnimData,
    nStrip: number = 0,
    smidNotify: number = ksmidNull,
  ): boolean {
    this.m_wx = wx;
    this.m_wy = wy;

    this.m_ani.Init(panid);
    this.StartAnimation(this.m_ani, nStrip, 0, 0);

    this.m_wfAnm = wfAnm & 0xffff;
    this.m_smidNotify = smidNotify;

    if (this.m_wfAnm & kfAnmSmokeFireLayer) this.m_ff = (this.m_ff | kfGobLayerSmokeFire) >>> 0;
    else if (this.m_wfAnm & kfAnmSurfaceDecalLayer)
      this.m_ff = (this.m_ff | kfGobLayerSurfaceDecal) >>> 0;
    else this.m_ff = (this.m_ff | kfGobLayerDepthSorted) >>> 0;

    this.ctx.gobm.AddGob(this);
    return true;
  }

  // issue #98: unsavable in the C++-FAITHFUL format, but carried in the TS COMPLETE snapshot —
  // it holds a gid + sits in the master list (shifts TrackState), so dropping it mid-life would
  // diverge the resumed sim. ctx.snapshotComplete is only set during snapshot()/restore().
  IsSavable(): boolean {
    return this.ctx.snapshotComplete;
  } // :916-919

  // Serialize the anim fields ONLY for a PURE AnimGob. The savable subclasses (PuffGob/ActivatorGob/
  // SmokeGob) have their OWN SaveState that calls super expecting Gob's serializer directly ("AnimGob
  // has no serializer" — Processor.cpp:602+); intercepting that corrupts their faithful save. For a
  // subclass this is a pass-through to Gob; only a bare AnimGob (kgtAnimation) carries its anim state.
  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (this.GetType() === kgtAnimation) {
      pstm.WriteByte(1);
      pstm.WriteString(this.m_ani.GetData()!.name); // the anim is chosen at spawn → save its name
      pstm.WriteWord(this.m_wfAnm);
      pstm.WriteDword(this.m_smidNotify);
      this.m_ani.SaveState(pstm);
    }
    return super.SaveState(pstm);
  }

  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (this.GetType() === kgtAnimation) {
      if (pstm.ReadByte() !== 1) return false;
      const aniName = pstm.ReadString(32);
      this.m_wfAnm = pstm.ReadWord();
      this.m_smidNotify = pstm.ReadDword() | 0;
      this.m_ani.Init(this.ctx.animDataFor(aniName));
      this.m_ani.LoadState(pstm);
    }
    return super.LoadState(pstm);
  }

  OnStripDone(): boolean {
    return false;
  } // :921-924

  // game/GameObjects.cpp:962-989
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      if (pmsg.mid === kmidReservedUpdate) {
        if (!this.AdvanceAnimation(this.m_ani)) {
          if (this.m_wfAnm & kfAnmLoop)
            this.StartAnimation(this.m_ani, this.m_ani.GetStrip(), 0, 0);

          if (this.m_smidNotify !== ksmidNull) {
            this.ctx.smm.SendMsgFrom(kmidAnimationComplete, this.m_gid, this.m_smidNotify);
          }

          const fDelete = this.OnStripDone();
          if (!fDelete) this.m_unvl.MinSkip();

          if (fDelete || this.m_wfAnm & kfAnmDeleteWhenDone) {
            this.ctx.gobm.RemoveGob(this);
            return knDeleted;
          }
        }
        return knHandled;
      }
      return knNotHandled;
    }
    return knNotHandled;
  }
}

// game/GameObjects.cpp:837-854
export function CreateAnimGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wfAnm: number,
  panid: AnimData,
  nStrip: number = 0,
  smidNotify: number = ksmidNull,
): AnimGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new AnimGob(ctx);
  if (!pgob.Init(wx, wy, wfAnm, panid, nStrip, smidNotify)) return null;
  return pgob;
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
// issue #98: registered now — the TS COMPLETE snapshot serializes live AnimGobs.
registerGobType(kgtAnimation, (ctx) => new AnimGob(ctx));
